// Profile Page
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Shield, Key, Save, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../lib/store';
import { authApi } from '../lib/api';
import { roleLabels, getInitials } from '../lib/utils';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

export function ProfilPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useAuthStore();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [passwords, setPasswords] = useState({
    altes_passwort: '',
    neues_passwort: '',
    neues_passwort_confirm: '',
  });

  // Auto-open password dialog if required
  useEffect(() => {
    if (searchParams.get('change_password') === 'true' || user?.muss_passwort_aendern) {
      setPasswordDialogOpen(true);
      setMustChangePassword(true);
    }
  }, [searchParams, user?.muss_passwort_aendern]);

  const handleChangePassword = async () => {
    if (passwords.neues_passwort !== passwords.neues_passwort_confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (passwords.neues_passwort.length < 12) {
      toast.error('Passwort muss mindestens 12 Zeichen lang sein');
      return;
    }

    setIsChangingPassword(true);
    try {
      await authApi.changePassword({
        altes_passwort: passwords.altes_passwort,
        neues_passwort: passwords.neues_passwort,
      });
      toast.success('Passwort erfolgreich geändert');
      setPasswordDialogOpen(false);
      setMustChangePassword(false);
      setPasswords({ altes_passwort: '', neues_passwort: '', neues_passwort_confirm: '' });
      
      // Update user state to reflect password change
      if (user) {
        setUser({ ...user, muss_passwort_aendern: false });
      }
      
      // Navigate to dashboard after password change
      if (mustChangePassword) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Password change error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Passwort ändern fehlgeschlagen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-2xl" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold">Mein Profil</h1>
          <p className="text-muted-foreground">
            Ihre persönlichen Informationen
          </p>
        </div>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {getInitials(user?.vorname, user?.nachname)}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl">
                  {user?.vorname} {user?.nachname}
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Badge>{roleLabels[user?.rolle]}</Badge>
                  {user?.dienstgrad && <span>{user?.dienstgrad}</span>}
                  {user?.qualifikation && <span>• {user?.qualifikation}</span>}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Login Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <User className="w-5 h-5" />
                Anmeldedaten
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Loginname</Label>
                  <Input value={user?.loginname || ''} disabled className="font-mono" />
                </div>
                <div className="space-y-2">
                  <Label>Passwort</Label>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setPasswordDialogOpen(true)}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Passwort ändern
                  </Button>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Kontaktdaten
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>E-Mail</Label>
                  <Input value={user?.email || '—'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input value={user?.telefon || '—'} disabled />
                </div>
              </div>
            </div>

            {/* Qualifications */}
            {user?.fahrzeug_lizenzen?.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Fahrzeug-Lizenzen
                </h3>
                <div className="flex flex-wrap gap-2">
                  {user.fahrzeug_lizenzen.map((lizenz, i) => (
                    <Badge key={i} variant="outline">{lizenz}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Password Change Dialog */}
        <Dialog 
          open={passwordDialogOpen} 
          onOpenChange={(open) => {
            // Prevent closing if password change is required
            if (!open && mustChangePassword) {
              toast.warning('Sie müssen Ihr Passwort ändern, bevor Sie fortfahren können');
              return;
            }
            setPasswordDialogOpen(open);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {mustChangePassword && <AlertTriangle className="w-5 h-5 text-amber-500" />}
                Passwort ändern
              </DialogTitle>
              <DialogDescription>
                {mustChangePassword 
                  ? 'Sie müssen Ihr temporäres Passwort ändern, bevor Sie fortfahren können.'
                  : 'Geben Sie Ihr aktuelles und neues Passwort ein'
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{mustChangePassword ? 'Temporäres Passwort' : 'Aktuelles Passwort'}</Label>
                <Input
                  type="password"
                  value={passwords.altes_passwort}
                  onChange={(e) => setPasswords(p => ({ ...p, altes_passwort: e.target.value }))}
                  placeholder={mustChangePassword ? 'Ihr temporäres Passwort' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>Neues Passwort</Label>
                <Input
                  type="password"
                  value={passwords.neues_passwort}
                  onChange={(e) => setPasswords(p => ({ ...p, neues_passwort: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Neues Passwort bestätigen</Label>
                <Input
                  type="password"
                  value={passwords.neues_passwort_confirm}
                  onChange={(e) => setPasswords(p => ({ ...p, neues_passwort_confirm: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              {!mustChangePassword && (
                <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                  Abbrechen
                </Button>
              )}
              <Button onClick={handleChangePassword} disabled={isChangingPassword}>
                {isChangingPassword ? 'Speichere...' : 'Passwort ändern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}

export default ProfilPage;
